import { useEffect, useId, useRef, useState } from 'react'
import type { ColumnSelection } from './columnFilters'

export type ColumnFilterConfig = {
  options: { value: string; label: string }[]
  selected: ColumnSelection
  onChange: (selected: ColumnSelection) => void
}

const SEARCH_FROM = 8

/**
 * The arrow in a column heading that opens that column's values as a checklist,
 * the way a spreadsheet's filter does. Unchecking a value hides its rows; a
 * long list can be searched. The list sits in a fixed layer so the table's
 * scroll region does not clip it.
 */
export default function ColumnFilter({ label, options, selected, onChange }: { label: string } & ColumnFilterConfig) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [anchor, setAnchor] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const button = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const id = useId()
  const active = selected !== undefined

  function close(returnFocus: boolean) {
    setOpen(false)
    setQuery('')
    if (returnFocus) button.current?.focus()
  }

  useEffect(() => {
    if (!open) return
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node
      if (!panel.current?.contains(target) && !button.current?.contains(target)) close(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close(true)
    }
    const away = (event: Event) => {
      if (event.target instanceof Node && panel.current?.contains(event.target)) return
      close(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', away)
    window.addEventListener('scroll', away, true)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', away)
      window.removeEventListener('scroll', away, true)
    }
  }, [open])

  function toggleOpen() {
    if (open) return close(true)
    const rect = button.current?.getBoundingClientRect()
    if (rect) setAnchor({ top: rect.bottom + 4, left: Math.max(8, Math.min(rect.left, window.innerWidth - 272)) })
    setOpen(true)
  }

  // Only the values the column offers now count: after the rows change (another
  // date, say) a value chosen earlier may no longer be among them.
  const isChecked = (value: string) => !selected || selected.includes(value)
  function toggle(value: string) {
    const current = options.map((option) => option.value).filter(isChecked)
    const next = isChecked(value) ? current.filter((candidate) => candidate !== value) : [...current, value]
    onChange(next.length === options.length ? undefined : next)
  }

  const shown = options.filter((option) => option.label.toLowerCase().includes(query.trim().toLowerCase()))
  const buttonClass = 'text-small font-bold hover:bg-surface-2 rounded-control px-2 min-h-8 border border-line-strong'
  return (
    <>
      <button
        ref={button}
        type="button"
        aria-label={active ? `Filter ${label} (filtered)` : `Filter ${label}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={toggleOpen}
        className={`ml-1 inline-flex size-6 items-center justify-center rounded-control border ${active ? 'border-primary bg-selected text-ink' : 'border-line-strong text-ink-muted hover:bg-surface-2'}`}
      >
        <svg aria-hidden="true" viewBox="0 0 12 12" className="size-2.5" fill="currentColor">
          <path d="M1 3h10L6 9z" />
        </svg>
      </button>
      {open && (
        <div
          ref={panel}
          id={id}
          role="dialog"
          aria-label={`Filter ${label}`}
          style={{ position: 'fixed', top: anchor.top, left: anchor.left, width: 264 }}
          className="z-50 flex flex-col gap-2 rounded-card border border-line-strong bg-canvas p-3 text-left font-normal tracking-normal normal-case shadow-lg"
        >
          {options.length >= SEARCH_FROM && (
            <input
              type="search"
              aria-label={`Search ${label} values`}
              placeholder="Search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="min-h-11 w-full rounded-control border border-line-strong bg-canvas px-3 text-small"
            />
          )}
          <div className="flex gap-2">
            <button type="button" onClick={() => onChange(undefined)} className={buttonClass}>
              Select all
            </button>
            <button type="button" onClick={() => onChange([])} className={buttonClass}>
              Clear all
            </button>
          </div>
          <ul className="flex max-h-60 flex-col overflow-y-auto">
            {shown.map((option) => (
              <li key={option.value}>
                <label className="flex min-h-9 items-center gap-2 text-small font-semibold">
                  <input type="checkbox" checked={isChecked(option.value)} onChange={() => toggle(option.value)} className="size-5" />
                  {option.label}
                </label>
              </li>
            ))}
            {shown.length === 0 && <li className="py-2 text-small text-ink-muted">No values match.</li>}
          </ul>
        </div>
      )}
    </>
  )
}
